

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api/axios";
import { runBackgroundRequest } from "../utils/backgroundRequestQueue";
import {
  CHAT_INBOX_CONNECTION_EVENT,
  CHAT_POLL_INTERVAL_MS,
  CHAT_REALTIME_POLL_INTERVAL_MS,
} from "../constants/chatPolling";

/**
 * ChatUnreadContext
 *  • Polls backend unread summary for internal chats
 *  • Exposes total_unread + per-chat map for fast lookups
 *
 * Backend endpoint:
 *   GET /chats/internal-chat/unread-summary/
 * Response:
 *   { total_unread: number, chats: [{ chat_id, event_id, unread_count, ... }, ...] }
 */

export const ChatUnreadContext = createContext(null);

const clampInt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
};

export const formatUnreadBadge = (count) => {
  const n = clampInt(count);
  if (n <= 0) return "";
  if (n > 999) return "999+";
  return String(n);
};

export const ChatUnreadProvider = ({
  children,
  enabled = true,
  intervalMs = CHAT_POLL_INTERVAL_MS,
  realtimeIntervalMs = CHAT_REALTIME_POLL_INTERVAL_MS,
}) => {
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const realtimeConnectedRef = useRef(false);
  const { data, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["chat_unread_summary"],
    enabled: !!enabled,
    queryFn: async () => {
      const res = await runBackgroundRequest(() => (
        api.get("/chats/internal-chat/unread-summary/", {
          meta: { suppressGlobalError: true },
        })
      ));
      return res?.data || { total_unread: 0, chats: [] };
    },
    refetchInterval: realtimeConnected ? realtimeIntervalMs : intervalMs,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const handleConnectionChange = (event) => {
      const connected = event?.detail?.connected === true;
      const wasConnected = realtimeConnectedRef.current;
      realtimeConnectedRef.current = connected;
      setRealtimeConnected(connected);

      // A reconnect may follow messages missed while the socket was down.
      if (connected && !wasConnected && enabled) {
        refetch();
      }
    };

    window.addEventListener(CHAT_INBOX_CONNECTION_EVENT, handleConnectionChange);
    return () => {
      window.removeEventListener(CHAT_INBOX_CONNECTION_EVENT, handleConnectionChange);
    };
  }, [enabled, refetch]);

  const value = useMemo(() => {
    const totalUnread = clampInt(data?.total_unread);
    const chatsArr = Array.isArray(data?.chats) ? data.chats : [];

    const byChatId = {};
    const byEventId = {};

    chatsArr.forEach((c) => {
      const chatId = c?.chat_id != null ? String(c.chat_id) : null;
      const eventId = c?.event_id != null ? String(c.event_id) : null;
      const unread = clampInt(c?.unread_count);

      if (chatId) byChatId[chatId] = unread;
      if (eventId) byEventId[eventId] = unread;
    });

    return {
      totalUnread,
      unreadReady: data != null,
      unreadUpdatedAt: dataUpdatedAt,
      byChatId,
      byEventId,
      formatUnreadBadge,
      refresh: refetch,
    };
  }, [data, refetch, dataUpdatedAt]);

  return (
    <ChatUnreadContext.Provider value={value}>
      {children}
    </ChatUnreadContext.Provider>
  );
};

export const useChatUnread = () => {
  const ctx = useContext(ChatUnreadContext);
  return (
    ctx || {
      totalUnread: 0,
      byChatId: {},
      byEventId: {},
      formatUnreadBadge,
      refresh: () => Promise.resolve(),
    }
  );
};
